import { useEffect, useMemo, useState } from "react";
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import Switch from "@mui/material/Switch";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import MenuItem from "@mui/material/MenuItem";
import Tooltip from "@mui/material/Tooltip";
import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import { toast } from "react-toastify";
import PropTypes from "prop-types";
import { useNavigate } from "react-router-dom";

import ArgonBox from "components/ArgonBox";
import ArgonTypography from "components/ArgonTypography";
import ArgonButton from "components/ArgonButton";
import ArgonInput from "components/ArgonInput";
import ArgonAvatar from "components/ArgonAvatar";
import ArgonBadge from "components/ArgonBadge";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import Table from "examples/Tables/Table";
import ApnaTabs from "components/ApnaTabs/ApnaTabs";
import PaginationBar from "components/PaginationBar/PaginationBar";
import Dropdown from "components/CustomDropdown/Dropdown";
import api from "utils/api";
import {
  assignApprovedEligibility,
  createGamificationSetting,
  evaluateGamification,
  getBadgeEligibilities,
  getBadgeEligibilityStats,
  getGamificationScores,
  getGamificationSettings,
  reviewBadgeEligibility,
  seedDefaultGamificationSettings,
  manualAssignBadge,
  updateGamificationSetting,
} from "services/gamificationService";

const metricOptions = [
  { value: "TEACHING_HOURS", label: "Teaching Hours Champion", metric: "totalTeachingHours" },
  { value: "HOMEWORK_CONSISTENCY", label: "Homework Consistency", metric: "homeworkPercentage" },
  {
    value: "ATTENDANCE_EXCELLENCE",
    label: "Attendance Excellence",
    metric: "professionalAttendanceScore",
  },
];

const monthOptions = [
  { value: 1, label: "January" },
  { value: 2, label: "February" },
  { value: 3, label: "March" },
  { value: 4, label: "April" },
  { value: 5, label: "May" },
  { value: 6, label: "June" },
  { value: 7, label: "July" },
  { value: 8, label: "August" },
  { value: 9, label: "September" },
  { value: 10, label: "October" },
  { value: 11, label: "November" },
  { value: 12, label: "December" },
];

const statusColor = {
  PENDING: "warning",
  APPROVED: "info",
  REJECTED: "error",
  ASSIGNED: "success",
  ACTIVE: "success",
  REMOVED: "error",
  EXPIRED: "secondary",
};

const currentMonth = new Date().getMonth() + 1;
const currentYear = new Date().getFullYear();

const emptySetting = {
  badgeName: "",
  subtitle: "",
  badgeDescription: "",
  criteria: "",
  metricType: "TEACHING_HOURS",
  conditionMetric: "totalTeachingHours",
  operator: ">=",
  value: "100",
  includeRecoveryClasses: true,
  includeDemoClasses: false,
  graceMinutes: "10",
  latePenalty: "2",
  missedPenalty: "5",
  recoveryBonus: "1",
  priority: "1",
  isActive: true,
};

const metricLabel = (metricType) =>
  metricOptions.find((option) => option.value === metricType)?.label || metricType || "N/A";

const tutorName = (record) => record?.tutorId?.fullName || record?.tutorId?.email || "N/A";
const tutorPhone = (record) => record?.tutorId?.phone || "N/A";
const tutorProfileId = (record) => record?.tutorId?._id || record?.tutorId?.id || "";
const monthLabel = (month) =>
  monthOptions.find((option) => Number(option.value) === Number(month))?.label || month || "N/A";

const formatDateTime = (value) => {
  if (!value) return "Not evaluated yet";
  return new Date(value).toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const fieldSx = {
  "& .MuiOutlinedInput-root": {
    minHeight: 42,
    borderRadius: "8px",
    backgroundColor: "#fff",
  },
  "& .MuiInputBase-input": {
    fontSize: "0.875rem",
    color: "#344767",
    padding: "10px 12px",
  },
};

const toolbarFieldSx = {
  "& .MuiOutlinedInput-root": {
    minHeight: 40,
    borderRadius: "8px",
    backgroundColor: "#fff",
  },
  "& .MuiInputBase-input": {
    fontSize: "0.875rem",
    color: "#344767",
    padding: "9px 12px",
  },
};

const FieldLabel = ({ children }) => (
  <ArgonTypography variant="caption" fontWeight="bold" color="text" display="block" mb={0.75}>
    {children}
  </ArgonTypography>
);

FieldLabel.propTypes = {
  children: PropTypes.node.isRequired,
};

function Gamification({ mode = "eligibility" }) {
  const navigate = useNavigate();
  const activeTab = mode === "settings" ? "Settings" : "Eligibility";
  const [settings, setSettings] = useState([]);
  const [eligibilities, setEligibilities] = useState([]);
  const [tutors, setTutors] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [totalItems, setTotalItems] = useState(0);
  const [eligibilityStatus, setEligibilityStatus] = useState("PENDING");
  const [searchTerm, setSearchTerm] = useState("");
  const [lastEvaluatedAt, setLastEvaluatedAt] = useState(null);
  const [period, setPeriod] = useState({ month: currentMonth, year: currentYear });
  const [settingDialog, setSettingDialog] = useState(false);
  const [editingSetting, setEditingSetting] = useState(null);
  const [settingForm, setSettingForm] = useState(emptySetting);
  const [reviewDialog, setReviewDialog] = useState(false);
  const [selectedEligibility, setSelectedEligibility] = useState(null);
  const [evaluateConfirmDialog, setEvaluateConfirmDialog] = useState(false);
  const [assignConfirmDialog, setAssignConfirmDialog] = useState(false);
  const [assignEligibility, setAssignEligibility] = useState(null);
  const [adminRemark, setAdminRemark] = useState("");
  const [defaultConfirmDialog, setDefaultConfirmDialog] = useState(false);
  const [manualAssignDialog, setManualAssignDialog] = useState(false);
  const [manualAssignForm, setManualAssignForm] = useState({
    tutorId: "",
    gamificationSettingId: "",
    month: currentMonth,
    year: currentYear,
    finalScore: "",
    adminRemark: "",
  });

  const fetchSettings = async () => {
    try {
      const response = await getGamificationSettings();
      if (response.success) setSettings(response.data || []);
    } catch (error) {
      toast.error(error.message || "Failed to fetch gamification settings");
    }
  };

  const fetchStats = async () => {
    try {
      const params = {
        month: Number(period.month),
        year: Number(period.year),
      };
      if (searchTerm.trim()) params.search = searchTerm.trim();
      const response = await getBadgeEligibilityStats(params);
      if (response.success) setStats(response.data || {});
    } catch (error) {
      console.error("Gamification stats error:", error);
    }
  };

  const fetchTutors = async () => {
    try {
      const approvedTutors = [];
      let tutorPage = 1;
      let hasNextPage = true;

      while (hasNextPage) {
        const response = await api.get("/tutor", {
          params: {
            approvalStatus: "APPROVED",
            page: tutorPage,
            limit: 100,
            sort: "fullName",
            order: "asc",
          },
        });

        if (!response.data.success) break;

        approvedTutors.push(...(response.data.data.tutors || []));
        hasNextPage = Boolean(response.data.data.pagination?.hasNextPage);
        tutorPage += 1;
      }

      setTutors(approvedTutors.filter((tutor) => tutor.active !== false));
    } catch (error) {
      console.error("Fetch tutors error:", error);
      toast.error("Failed to fetch tutors");
    }
  };

  const fetchTabData = async () => {
    setLoading(true);
    try {
      if (activeTab === "Eligibility") {
        const params = {
          page,
          limit: rowsPerPage,
          status: eligibilityStatus,
          month: Number(period.month),
          year: Number(period.year),
        };
        if (searchTerm.trim()) params.search = searchTerm.trim();
        const response = await getBadgeEligibilities(params);
        if (response.success) {
          setEligibilities(response.data || []);
          setTotalItems(response.pagination?.total || 0);
        }
      }
    } catch (error) {
      toast.error(error.message || "Failed to fetch gamification data");
    } finally {
      setLoading(false);
    }
  };

  const fetchLastEvaluatedAt = async () => {
    try {
      const response = await getGamificationScores({
        month: Number(period.month),
        year: Number(period.year),
        page: 1,
        limit: 1,
      });
      const latestScore = response.success ? response.data?.[0] : null;
      setLastEvaluatedAt(latestScore?.calculatedAt || latestScore?.updatedAt || null);
    } catch (error) {
      console.error("Last evaluated fetch error:", error);
      setLastEvaluatedAt(null);
    }
  };

  useEffect(() => {
    fetchSettings();
    fetchStats();
    fetchTutors();
  }, []);

  useEffect(() => {
    if (activeTab !== "Settings") fetchTabData();
  }, [activeTab, page, rowsPerPage, eligibilityStatus, period.month, period.year, searchTerm]);

  useEffect(() => {
    if (activeTab !== "Settings") fetchStats();
  }, [activeTab, period.month, period.year, searchTerm]);

  useEffect(() => {
    if (activeTab !== "Settings") fetchLastEvaluatedAt();
  }, [activeTab, period.month, period.year]);

  const eligibilityStatusTabs = [
    { label: "PENDING", count: stats.pending || 0, color: "#f59e0b", content: "" },
    { label: "APPROVED", count: stats.approved || 0, color: "#0ea5e9", content: "" },
    { label: "REJECTED", count: stats.rejected || 0, color: "#ef4444", content: "" },
    { label: "ASSIGNED", count: stats.assigned || 0, color: "#22c55e", content: "" },
  ];

  const openCreateSetting = () => {
    setEditingSetting(null);
    setSettingForm(emptySetting);
    setSettingDialog(true);
  };

  const openEditSetting = (setting) => {
    const firstCondition = setting.conditions?.[0] || {};
    setEditingSetting(setting);
    setSettingForm({
      badgeName: setting.badgeName || "",
      subtitle: setting.subtitle || "",
      badgeDescription: setting.badgeDescription || "",
      criteria: setting.criteria || "",
      metricType: setting.metricType || "TEACHING_HOURS",
      conditionMetric: firstCondition.metric || "totalTeachingHours",
      operator: firstCondition.operator || ">=",
      value: (firstCondition.value ?? "").toString(),
      includeRecoveryClasses: setting.includeRecoveryClasses !== false,
      includeDemoClasses: Boolean(setting.includeDemoClasses),
      graceMinutes: (setting.graceMinutes ?? 10).toString(),
      latePenalty: (setting.latePenalty ?? 2).toString(),
      missedPenalty: (setting.missedPenalty ?? 5).toString(),
      recoveryBonus: (setting.recoveryBonus ?? 1).toString(),
      priority: (setting.priority ?? 1).toString(),
      isActive: Boolean(setting.isActive),
    });
    setSettingDialog(true);
  };

  const handleMetricChange = (metricType) => {
    const metric = metricOptions.find((option) => option.value === metricType)?.metric;
    setSettingForm((prev) => ({ ...prev, metricType, conditionMetric: metric || prev.conditionMetric }));
  };

  const saveSetting = async () => {
    if (!settingForm.badgeName.trim() || !settingForm.badgeDescription.trim()) {
      toast.error("Badge name and description are required");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        badgeName: settingForm.badgeName.trim(),
        subtitle: settingForm.subtitle.trim(),
        badgeDescription: settingForm.badgeDescription.trim(),
        criteria: settingForm.criteria.trim(),
        metricType: settingForm.metricType,
        conditions: [
          {
            metric: settingForm.conditionMetric,
            operator: settingForm.operator,
            value: Number(settingForm.value),
          },
        ],
        includeRecoveryClasses: settingForm.includeRecoveryClasses,
        includeDemoClasses: settingForm.includeDemoClasses,
        graceMinutes: Number(settingForm.graceMinutes),
        latePenalty: Number(settingForm.latePenalty),
        missedPenalty: Number(settingForm.missedPenalty),
        recoveryBonus: Number(settingForm.recoveryBonus),
        priority: Number(settingForm.priority),
        isActive: settingForm.isActive,
      };
      const response = editingSetting
        ? await updateGamificationSetting(editingSetting._id, payload)
        : await createGamificationSetting(payload);
      if (response.success) {
        toast.success(editingSetting ? "Setting updated" : "Setting created");
        setSettingDialog(false);
        fetchSettings();
      }
    } catch (error) {
      toast.error(error.message || "Failed to save setting");
    } finally {
      setSaving(false);
    }
  };

  const seedDefaults = async () => {
    setSaving(true);
    try {
      const response = await seedDefaultGamificationSettings();
      if (response.success) {
        toast.success("Default settings saved");
        setDefaultConfirmDialog(false);
        fetchSettings();
      }
    } catch (error) {
      toast.error(error.message || "Failed to save default settings");
    } finally {
      setSaving(false);
    }
  };

  const runEvaluation = async () => {
    setEvaluateConfirmDialog(false);
    setSaving(true);
    try {
      const response = await evaluateGamification({
        month: Number(period.month),
        year: Number(period.year),
      });
      if (response.success) {
        toast.success("Evaluation completed");
        fetchStats();
        fetchTabData();
        fetchLastEvaluatedAt();
      }
    } catch (error) {
      toast.error(error.message || "Evaluation failed");
    } finally {
      setSaving(false);
    }
  };

  const reviewEligibility = async (status) => {
    if (!selectedEligibility) return;
    setSaving(true);
    try {
      const response = await reviewBadgeEligibility(selectedEligibility._id, {
        status,
        adminRemark,
      });
      if (response.success) {
        toast.success(response.message || "Eligibility reviewed");
        setReviewDialog(false);
        setAdminRemark("");
        fetchStats();
        fetchTabData();
      }
    } catch (error) {
      toast.error(error.message || "Failed to review eligibility");
    } finally {
      setSaving(false);
    }
  };

  const openAssignConfirmDialog = (record) => {
    setAssignEligibility(record);
    setAssignConfirmDialog(true);
  };

  const handleAssignApprovedEligibility = async () => {
    if (!assignEligibility) return;
    setSaving(true);
    try {
      const response = await assignApprovedEligibility(assignEligibility._id);
      if (response.success) {
        toast.success(response.message || "Badge assigned successfully");
        setAssignConfirmDialog(false);
        setAssignEligibility(null);
        setEligibilityStatus("ASSIGNED");
        setPage(1);
        fetchStats();
      }
    } catch (error) {
      toast.error(error.message || "Failed to assign badge");
    } finally {
      setSaving(false);
    }
  };

  const openManualAssignDialog = () => {
    setManualAssignForm({
      tutorId: "",
      gamificationSettingId: "",
      month: period.month,
      year: period.year,
      finalScore: "",
      adminRemark: "",
    });
    setManualAssignDialog(true);
  };

  const handleManualAssign = async () => {
    if (!manualAssignForm.tutorId || !manualAssignForm.gamificationSettingId) {
      toast.error("Please select tutor and badge");
      return;
    }

    setSaving(true);
    try {
      const response = await manualAssignBadge({
        tutorId: manualAssignForm.tutorId,
        gamificationSettingId: manualAssignForm.gamificationSettingId,
        month: Number(manualAssignForm.month),
        year: Number(manualAssignForm.year),
        finalScore: Number(manualAssignForm.finalScore || 0),
        adminRemark: manualAssignForm.adminRemark,
      });

      if (response.success) {
        toast.success("Badge assigned successfully");
        setManualAssignDialog(false);
        setEligibilityStatus("ASSIGNED");
        fetchStats();
        fetchTabData();
      }
    } catch (error) {
      toast.error(error.message || "Failed to assign badge");
    } finally {
      setSaving(false);
    }
  };

  const settingsRows = settings.map((setting, index) => ({
    sno: index + 1,
    Badge: (
      <ArgonBox>
        <ArgonTypography variant="caption" fontWeight="bold">
          {setting.badgeName}
        </ArgonTypography>
        <ArgonTypography variant="caption" display="block" color="secondary">
          {setting.subtitle || metricLabel(setting.metricType)}
        </ArgonTypography>
      </ArgonBox>
    ),
    Metric: metricLabel(setting.metricType),
    Criteria: (
      <ArgonTypography variant="caption" color="text">
        {setting.criteria}
      </ArgonTypography>
    ),
    Condition: `${setting.conditions?.[0]?.metric || "N/A"} ${setting.conditions?.[0]?.operator || ""} ${
      setting.conditions?.[0]?.value ?? ""
    }`,
    Status: (
      <ArgonBadge
        variant="gradient"
        badgeContent={setting.isActive ? "ACTIVE" : "INACTIVE"}
        color={setting.isActive ? "success" : "secondary"}
        size="xs"
        container
      />
    ),
    Action: (
      <Button variant="text" size="large" sx={{ minWidth: 32, p: 0 }} onClick={() => openEditSetting(setting)}>
        <EditIcon color="primary" />
      </Button>
    ),
  }));

  const renderTutorProfile = (record) => {
    const tutorId = tutorProfileId(record);
    const handleProfileClick = () => {
      if (tutorId) navigate(`/tutors/profile/${tutorId}`);
    };

    return (
      <ArgonBox display="flex" alignItems="center" px={0.5} py={0.1}>
        <ArgonBox mr={2}>
          <ArgonAvatar
            src={record?.tutorId?.profileImage || ""}
            alt={tutorName(record)}
            size="sm"
            variant="rounded"
          />
        </ArgonBox>
        <ArgonBox display="flex" flexDirection="column">
          <ArgonTypography
            textTransform="none"
            variant="button"
            fontWeight="medium"
            onClick={handleProfileClick}
            sx={{
              cursor: tutorId ? "pointer" : "default",
              textDecoration: tutorId ? "underline" : "none",
              "&:hover": tutorId
                ? {
                    color: "#1976d2",
                    textDecoration: "underline",
                  }
                : {},
            }}
          >
            {tutorName(record)}
          </ArgonTypography>
          <ArgonTypography variant="caption" color="secondary">
            {tutorPhone(record)}
          </ArgonTypography>
        </ArgonBox>
      </ArgonBox>
    );
  };

  const eligibilityRows = eligibilities.map((record, index) => ({
    sno: (page - 1) * rowsPerPage + index + 1,
    Tutor: renderTutorProfile(record),
    MobileNo: (
      <ArgonTypography variant="caption" fontWeight="medium" color="text">
        {tutorPhone(record)}
      </ArgonTypography>
    ),
    Badge: record.badgeName,
    Metric: metricLabel(record.metricType),
    Score: record.score,
    Period: `${monthLabel(record.month)} ${record.year}`,
    Status: (
      <ArgonBadge
        variant="gradient"
        badgeContent={record.status}
        color={statusColor[record.status] || "secondary"}
        size="xs"
        container
      />
    ),
    Action: record.status === "PENDING" ? (
      <Tooltip title="Review eligibility">
        <IconButton
          color="info"
          size="small"
          onClick={() => {
            setSelectedEligibility(record);
            setReviewDialog(true);
          }}
          sx={{
            width: 34,
            height: 34,
            borderRadius: "8px",
            backgroundColor: "rgba(17, 113, 239, 0.08)",
            "&:hover": { backgroundColor: "rgba(17, 113, 239, 0.16)" },
          }}
        >
          <EditIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    ) : record.status === "APPROVED" ? (
      <Tooltip title="Assign badge">
        <IconButton
          color="success"
          size="small"
          onClick={() => openAssignConfirmDialog(record)}
          disabled={saving}
          sx={{
            width: 34,
            height: 34,
            borderRadius: "8px",
            backgroundColor: "rgba(45, 206, 137, 0.1)",
            "&:hover": { backgroundColor: "rgba(45, 206, 137, 0.18)" },
          }}
        >
          <EmojiEventsIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    ) : (
      <ArgonTypography variant="caption" color="secondary" fontWeight="medium">
        {record.status === "ASSIGNED" ? "Assigned" : "Reviewed"}
      </ArgonTypography>
    ),
  }));

  const tableConfig = useMemo(() => {
    if (activeTab === "Settings") {
      return {
        columns: [
          { name: "sno", align: "center" },
          { name: "Badge", align: "left" },
          { name: "Metric", align: "center" },
          { name: "Criteria", align: "left" },
          { name: "Condition", align: "center" },
          { name: "Status", align: "center" },
          { name: "Action", align: "center" },
        ],
        rows: settingsRows,
      };
    }
    return {
      columns: [
        { name: "sno", align: "center" },
        { name: "Tutor", align: "left" },
        // { name: "MobileNo", align: "center" },
        { name: "Badge", align: "center" },
        { name: "Metric", align: "center" },
        { name: "Score", align: "center" },
        { name: "Period", align: "center" },
        { name: "Status", align: "center" },
        { name: "Action", align: "center" },
      ],
      rows: eligibilityRows,
    };
  }, [activeTab, settings, eligibilities]);

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <ArgonBox py={3}>
        <Card>
          <ArgonBox p={3}>
            <ArgonBox
              display="flex"
              alignItems={{ xs: "stretch", md: "center" }}
              justifyContent="space-between"
              flexDirection={{ xs: "column", md: "row" }}
              gap={2}
              mb={2}
            >
              <ArgonBox display="flex" alignItems="center" gap={1.5}>
                <EmojiEventsIcon color="primary" sx={{ fontSize: 32 }} />
                <ArgonBox>
                  <ArgonTypography variant="h5" fontWeight="medium">
                    {activeTab === "Settings" ? "Gamification Settings" : "Tutor Gamification"}
                  </ArgonTypography>
                  <ArgonTypography variant="caption" color="secondary">
                    {activeTab === "Settings"
                      ? "Manage badge criteria, thresholds, and active rules"
                      : "Review tutor badge eligibility by status"}
                  </ArgonTypography>
                  {activeTab === "Eligibility" && (
                    <ArgonTypography variant="caption" color="secondary" display="block" mt={0.5}>
                      Last evaluated: {formatDateTime(lastEvaluatedAt)}
                    </ArgonTypography>
                  )}
                </ArgonBox>
              </ArgonBox>
              {activeTab === "Eligibility" && (
                <ArgonBox display="flex" alignItems="center" justifyContent="flex-end" flexWrap="wrap" gap={1}>
                  <Dropdown
                    label="Month"
                    value={period.month}
                    width={132}
                    options={monthOptions}
                    onChange={(event) => {
                      setPeriod((prev) => ({ ...prev, month: event.target.value }));
                      setPage(1);
                    }}
                  />
                  <ArgonBox display="flex" alignItems="center" gap={0.75}>
                    <ArgonTypography variant="caption" fontWeight="bold" color="text">
                      Year
                    </ArgonTypography>
                    <ArgonInput
                      size="small"
                      type="number"
                      value={period.year}
                      onChange={(event) => {
                        setPeriod((prev) => ({ ...prev, year: event.target.value }));
                        setPage(1);
                      }}
                      sx={{ ...toolbarFieldSx, width: 108 }}
                    />
                  </ArgonBox>
                  <ArgonButton
                    color="primary"
                    variant="contained"
                    onClick={() => setEvaluateConfirmDialog(true)}
                    disabled={saving}
                    sx={{ minHeight: 40, px: 2.25, whiteSpace: "nowrap" }}
                  >
                    <PlayArrowIcon fontSize="small" />
                    &nbsp;Evaluate
                  </ArgonButton>
                  <ArgonButton
                    color="info"
                    variant="outlined"
                    onClick={openManualAssignDialog}
                    disabled={saving}
                    sx={{ minHeight: 40, px: 2.25, whiteSpace: "nowrap" }}
                  >
                    <AddIcon fontSize="small" />
                    &nbsp;Assign Badge
                  </ArgonButton>
                </ArgonBox>
              )}
              {activeTab === "Settings" && (
                <ArgonBox display="flex" alignItems="center" flexWrap="wrap" gap={1}>
                  <ArgonButton
                    color="secondary"
                    variant="outlined"
                    onClick={() => setDefaultConfirmDialog(true)}
                    disabled={saving}
                  >
                    <RestartAltIcon fontSize="small" />
                    &nbsp;Defaults
                  </ArgonButton>
                  <ArgonButton color="primary" variant="contained" onClick={openCreateSetting}>
                    <AddIcon fontSize="small" />
                    &nbsp;Add
                  </ArgonButton>
                </ArgonBox>
              )}
            </ArgonBox>

            {activeTab === "Eligibility" && (
              <>
                <ArgonBox
                  mt={2}
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                  gap={2}
                >
                  <ArgonBox sx={{ height: "36px", flex: "1 1 auto", minWidth: 0 }}>
                    <ApnaTabs
                      tabsData={eligibilityStatusTabs}
                      activeTab={eligibilityStatus}
                      onTabChange={(status) => {
                        setEligibilityStatus(status);
                        setPage(1);
                      }}
                    />
                  </ArgonBox>
                  <ArgonBox sx={{ height: "36px", flexShrink: 0 }} display="flex" alignItems="center">
                    <ArgonInput
                      size="small"
                      placeholder="search tutors..."
                      value={searchTerm}
                      onChange={(event) => {
                        setSearchTerm(event.target.value);
                        setPage(1);
                      }}
                      sx={{ ...toolbarFieldSx, width: 232 }}
                    />
                  </ArgonBox>
                </ArgonBox>
              </>
            )}
          </ArgonBox>

          <ArgonBox
            sx={{
              "& .MuiTableRow-root:not(:last-child) td": {
                borderBottom: ({ borders: { borderWidth, borderColor } }) =>
                  `${borderWidth[1]} solid ${borderColor}`,
              },
            }}
          >
            {loading && activeTab !== "Settings" ? (
              <ArgonBox textAlign="center" py={4}>
                <CircularProgress color="primary" />
                <ArgonTypography variant="body2" mt={1}>
                  Loading gamification data...
                </ArgonTypography>
              </ArgonBox>
            ) : tableConfig.rows.length ? (
              <Table columns={tableConfig.columns} rows={tableConfig.rows} />
            ) : (
              <ArgonBox textAlign="center" py={4}>
                <ArgonTypography variant="h6" color="text">
                  No records found
                </ArgonTypography>
              </ArgonBox>
            )}
          </ArgonBox>

          {activeTab !== "Settings" && totalItems > 0 && (
            <ArgonBox p={2}>
              <PaginationBar
                currentPage={page}
                rowsPerPage={rowsPerPage}
                totalItems={totalItems}
                onPageChange={(event, nextPage) => setPage(nextPage)}
                onRowsPerPageChange={(event) => {
                  setRowsPerPage(event.target.value);
                  setPage(1);
                }}
              />
            </ArgonBox>
          )}
        </Card>

        <Dialog open={settingDialog} onClose={() => setSettingDialog(false)} maxWidth="md" fullWidth>
          <DialogTitle>{editingSetting ? "Edit Gamification Setting" : "Add Gamification Setting"}</DialogTitle>
          <DialogContent>
            <Grid container spacing={2} mt={0.5}>
              <Grid item xs={12} md={6}>
                <FieldLabel>Badge Name</FieldLabel>
                <ArgonInput
                  fullWidth
                  value={settingForm.badgeName}
                  placeholder="Enter badge name"
                  sx={fieldSx}
                  onChange={(event) =>
                    setSettingForm((prev) => ({ ...prev, badgeName: event.target.value }))
                  }
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FieldLabel>Subtitle</FieldLabel>
                <ArgonInput
                  fullWidth
                  value={settingForm.subtitle}
                  placeholder="Enter subtitle"
                  sx={fieldSx}
                  onChange={(event) =>
                    setSettingForm((prev) => ({ ...prev, subtitle: event.target.value }))
                  }
                />
              </Grid>
              <Grid item xs={12}>
                <FieldLabel>Badge Description</FieldLabel>
                <ArgonInput
                  fullWidth
                  multiline
                  minRows={2}
                  value={settingForm.badgeDescription}
                  placeholder="Enter badge description"
                  sx={fieldSx}
                  onChange={(event) =>
                    setSettingForm((prev) => ({
                      ...prev,
                      badgeDescription: event.target.value,
                    }))
                  }
                />
              </Grid>
              <Grid item xs={12}>
                <FieldLabel>Criteria</FieldLabel>
                <ArgonInput
                  fullWidth
                  multiline
                  minRows={2}
                  value={settingForm.criteria}
                  placeholder="Enter criteria shown to admin"
                  sx={fieldSx}
                  onChange={(event) =>
                    setSettingForm((prev) => ({ ...prev, criteria: event.target.value }))
                  }
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <FieldLabel>Metric Type</FieldLabel>
                <TextField
                  select
                  fullWidth
                  value={settingForm.metricType}
                  sx={fieldSx}
                  onChange={(event) => handleMetricChange(event.target.value)}
                >
                  {metricOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} md={4}>
                <FieldLabel>Operator</FieldLabel>
                <TextField
                  select
                  fullWidth
                  value={settingForm.operator}
                  sx={fieldSx}
                  onChange={(event) =>
                    setSettingForm((prev) => ({ ...prev, operator: event.target.value }))
                  }
                >
                  {[">=", "<=", "==", ">", "<"].map((operator) => (
                    <MenuItem key={operator} value={operator}>
                      {operator}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} md={4}>
                <FieldLabel>Condition Value</FieldLabel>
                <ArgonInput
                  fullWidth
                  type="number"
                  value={settingForm.value}
                  placeholder="Enter value"
                  sx={fieldSx}
                  onChange={(event) =>
                    setSettingForm((prev) => ({ ...prev, value: event.target.value }))
                  }
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <FieldLabel>Grace Minutes</FieldLabel>
                <ArgonInput
                  fullWidth
                  type="number"
                  value={settingForm.graceMinutes}
                  sx={fieldSx}
                  onChange={(event) =>
                    setSettingForm((prev) => ({ ...prev, graceMinutes: event.target.value }))
                  }
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <FieldLabel>Late Penalty</FieldLabel>
                <ArgonInput
                  fullWidth
                  type="number"
                  value={settingForm.latePenalty}
                  sx={fieldSx}
                  onChange={(event) =>
                    setSettingForm((prev) => ({ ...prev, latePenalty: event.target.value }))
                  }
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <FieldLabel>Missed Penalty</FieldLabel>
                <ArgonInput
                  fullWidth
                  type="number"
                  value={settingForm.missedPenalty}
                  sx={fieldSx}
                  onChange={(event) =>
                    setSettingForm((prev) => ({ ...prev, missedPenalty: event.target.value }))
                  }
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <FieldLabel>Recovery Bonus</FieldLabel>
                <ArgonInput
                  fullWidth
                  type="number"
                  value={settingForm.recoveryBonus}
                  sx={fieldSx}
                  onChange={(event) =>
                    setSettingForm((prev) => ({ ...prev, recoveryBonus: event.target.value }))
                  }
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <FieldLabel>Priority</FieldLabel>
                <ArgonInput
                  fullWidth
                  type="number"
                  value={settingForm.priority}
                  sx={fieldSx}
                  onChange={(event) =>
                    setSettingForm((prev) => ({ ...prev, priority: event.target.value }))
                  }
                />
              </Grid>
              <Grid item xs={12} md={8}>
                <ArgonBox display="flex" alignItems="center" gap={2} height="100%">
                  <ArgonTypography variant="button">Recovery Classes</ArgonTypography>
                  <Switch checked={settingForm.includeRecoveryClasses} onChange={(event) => setSettingForm((prev) => ({ ...prev, includeRecoveryClasses: event.target.checked }))} />
                  <ArgonTypography variant="button">Demo Classes</ArgonTypography>
                  <Switch checked={settingForm.includeDemoClasses} onChange={(event) => setSettingForm((prev) => ({ ...prev, includeDemoClasses: event.target.checked }))} />
                  <ArgonTypography variant="button">Active</ArgonTypography>
                  <Switch checked={settingForm.isActive} onChange={(event) => setSettingForm((prev) => ({ ...prev, isActive: event.target.checked }))} />
                </ArgonBox>
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <ArgonButton variant="outlined" color="secondary" onClick={() => setSettingDialog(false)} disabled={saving}>
              Cancel
            </ArgonButton>
            <ArgonButton variant="contained" color="primary" onClick={saveSetting} disabled={saving}>
              {saving ? <CircularProgress size={18} color="inherit" /> : "Save"}
            </ArgonButton>
          </DialogActions>
        </Dialog>

        <Dialog
          open={evaluateConfirmDialog}
          onClose={() => setEvaluateConfirmDialog(false)}
          maxWidth="sm"
          fullWidth
          sx={{
            "& .MuiDialog-paper": {
              borderRadius: "16px",
              boxShadow: "0 20px 60px rgba(0,0,0,0.18)",
            },
          }}
        >
          <DialogTitle sx={{ pb: 1, position: "relative" }}>
            <ArgonBox display="flex" alignItems="center" gap={2}>
              <ArgonBox
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: "12px",
                  backgroundColor: "rgba(94, 114, 228, 0.12)",
                  color: "#5e72e4",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <PlayArrowIcon />
              </ArgonBox>
              <ArgonBox>
                <ArgonTypography variant="h5" color="text" fontWeight="medium">
                  Run Evaluation
                </ArgonTypography>
                <ArgonTypography variant="body2" color="secondary">
                  This will calculate tutor gamification for the selected period.
                </ArgonTypography>
              </ArgonBox>
            </ArgonBox>
            <IconButton
              onClick={() => setEvaluateConfirmDialog(false)}
              sx={{ position: "absolute", right: 8, top: 8, color: "#6c757d" }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent sx={{ py: 2.5 }}>
            <ArgonBox
              sx={{
                backgroundColor: "#f8fbff",
                border: "1px solid #d7e7ff",
                borderRadius: "8px",
                p: 2,
              }}
            >
              <ArgonTypography variant="body2" color="text" fontWeight="medium">
                Period: {monthLabel(period.month)} {period.year}
              </ArgonTypography>
              <ArgonTypography variant="caption" color="secondary" display="block" mt={1}>
                Existing eligibility for this period may be refreshed based on current teaching,
                homework, and attendance data.
              </ArgonTypography>
            </ArgonBox>
          </DialogContent>
          <DialogActions sx={{ p: 3, pt: 0, gap: 1.5 }}>
            <ArgonButton
              onClick={() => setEvaluateConfirmDialog(false)}
              variant="outlined"
              color="secondary"
              disabled={saving}
            >
              Cancel
            </ArgonButton>
            <ArgonButton onClick={runEvaluation} variant="contained" color="primary" disabled={saving}>
              <PlayArrowIcon fontSize="small" />
              &nbsp;{saving ? "Evaluating..." : "Evaluate"}
            </ArgonButton>
          </DialogActions>
        </Dialog>

        <Dialog open={reviewDialog} onClose={() => setReviewDialog(false)} maxWidth="sm" fullWidth>
          <DialogTitle>
            <ArgonTypography variant="h5" fontWeight="bold" color="dark">
              Review Badge Eligibility
            </ArgonTypography>
          </DialogTitle>
          <DialogContent>
            <ArgonBox
              p={2}
              mb={2}
              borderRadius="lg"
              sx={{ backgroundColor: "#f8f9fa", border: "1px solid #e9ecef" }}
            >
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <FieldLabel>Tutor</FieldLabel>
                  <ArgonTypography variant="button" fontWeight="medium">
                    {tutorName(selectedEligibility)}
                  </ArgonTypography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <FieldLabel>Badge</FieldLabel>
                  <ArgonTypography variant="button" fontWeight="medium">
                    {selectedEligibility?.badgeName || "N/A"}
                  </ArgonTypography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <FieldLabel>Score</FieldLabel>
                  <ArgonTypography variant="button" fontWeight="medium">
                    {selectedEligibility?.score ?? 0}
                  </ArgonTypography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <FieldLabel>Period</FieldLabel>
                  <ArgonTypography variant="button" fontWeight="medium">
                    {monthLabel(selectedEligibility?.month)} {selectedEligibility?.year}
                  </ArgonTypography>
                </Grid>
              </Grid>
            </ArgonBox>
            <FieldLabel>Admin Remark</FieldLabel>
            <ArgonInput
              fullWidth
              multiline
              minRows={3}
              placeholder="Enter remark for this review"
              value={adminRemark}
              onChange={(event) => setAdminRemark(event.target.value)}
              sx={fieldSx}
            />
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 3, gap: 1 }}>
            <ArgonButton
              variant="outlined"
              color="secondary"
              onClick={() => setReviewDialog(false)}
              disabled={saving}
            >
              Cancel
            </ArgonButton>
            <ArgonButton
              variant="outlined"
              color="error"
              onClick={() => reviewEligibility("REJECTED")}
              disabled={saving}
            >
              <CancelIcon fontSize="small" />
              &nbsp;Reject
            </ArgonButton>
            <ArgonButton
              variant="contained"
              color="success"
              onClick={() => reviewEligibility("APPROVED")}
              disabled={saving}
            >
              <CheckCircleIcon fontSize="small" />
              &nbsp;Approve
            </ArgonButton>
          </DialogActions>
        </Dialog>

        <Dialog
          open={manualAssignDialog}
          onClose={() => setManualAssignDialog(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            <ArgonTypography variant="h5" fontWeight="bold" color="dark">
              Assign Badge Manually
            </ArgonTypography>
          </DialogTitle>
          <DialogContent>
            <Grid container spacing={2} mt={0.5}>
              <Grid item xs={12}>
                <FieldLabel>Select Tutor</FieldLabel>
                <Dropdown
                  value={manualAssignForm.tutorId}
                  width="100%"
                  placeholder="Select Tutor"
                  options={tutors.map((tutor) => ({
                    value: tutor._id || tutor.id,
                    label: tutor.fullName || tutor.email || "N/A",
                  }))}
                  onChange={(event) =>
                    setManualAssignForm((prev) => ({ ...prev, tutorId: event.target.value }))
                  }
                />
              </Grid>
              <Grid item xs={12}>
                <FieldLabel>Select Badge</FieldLabel>
                <Dropdown
                  value={manualAssignForm.gamificationSettingId}
                  width="100%"
                  placeholder="Select Badge"
                  options={settings
                    .filter((setting) => setting.isActive)
                    .map((setting) => ({
                      value: setting._id,
                      label: `${setting.badgeName} - ${metricLabel(setting.metricType)}`,
                    }))}
                  onChange={(event) =>
                    setManualAssignForm((prev) => ({
                      ...prev,
                      gamificationSettingId: event.target.value,
                    }))
                  }
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FieldLabel>Month</FieldLabel>
                <Dropdown
                  value={manualAssignForm.month}
                  width="100%"
                  placeholder="Select Month"
                  options={monthOptions}
                  onChange={(event) =>
                    setManualAssignForm((prev) => ({ ...prev, month: event.target.value }))
                  }
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FieldLabel>Year</FieldLabel>
                <ArgonInput
                  fullWidth
                  type="number"
                  value={manualAssignForm.year}
                  sx={fieldSx}
                  onChange={(event) =>
                    setManualAssignForm((prev) => ({ ...prev, year: event.target.value }))
                  }
                />
              </Grid>
              <Grid item xs={12}>
                <FieldLabel>Final Score</FieldLabel>
                <ArgonInput
                  fullWidth
                  type="number"
                  value={manualAssignForm.finalScore}
                  placeholder="Optional score"
                  sx={fieldSx}
                  onChange={(event) =>
                    setManualAssignForm((prev) => ({ ...prev, finalScore: event.target.value }))
                  }
                />
              </Grid>
              <Grid item xs={12}>
                <FieldLabel>Admin Remark</FieldLabel>
                <ArgonInput
                  fullWidth
                  multiline
                  minRows={3}
                  value={manualAssignForm.adminRemark}
                  placeholder="Reason for manual badge assignment"
                  sx={fieldSx}
                  onChange={(event) =>
                    setManualAssignForm((prev) => ({ ...prev, adminRemark: event.target.value }))
                  }
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 3 }}>
            <ArgonButton
              variant="outlined"
              color="secondary"
              onClick={() => setManualAssignDialog(false)}
              disabled={saving}
            >
              Cancel
            </ArgonButton>
            <ArgonButton
              variant="contained"
              color="primary"
              onClick={handleManualAssign}
              disabled={saving}
            >
              Assign
            </ArgonButton>
          </DialogActions>
        </Dialog>

        <Dialog
          open={assignConfirmDialog}
          onClose={() => setAssignConfirmDialog(false)}
          maxWidth="sm"
          fullWidth
          sx={{
            "& .MuiDialog-paper": {
              borderRadius: "16px",
              boxShadow: "0 20px 60px rgba(0,0,0,0.18)",
            },
          }}
        >
          <DialogTitle sx={{ pb: 1, position: "relative" }}>
            <ArgonBox display="flex" alignItems="center" gap={2}>
              <ArgonBox
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: "12px",
                  backgroundColor: "rgba(45, 206, 137, 0.12)",
                  color: "#2dce89",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <EmojiEventsIcon />
              </ArgonBox>
              <ArgonBox>
                <ArgonTypography variant="h5" color="text" fontWeight="medium">
                  Assign Badge
                </ArgonTypography>
                <ArgonTypography variant="body2" color="secondary">
                  Please confirm before assigning this approved badge.
                </ArgonTypography>
              </ArgonBox>
            </ArgonBox>
            <IconButton
              onClick={() => setAssignConfirmDialog(false)}
              sx={{ position: "absolute", right: 8, top: 8, color: "#6c757d" }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent sx={{ py: 2.5 }}>
            <ArgonBox
              sx={{
                backgroundColor: "#f8fbff",
                border: "1px solid #d7e7ff",
                borderRadius: "8px",
                p: 2,
              }}
            >
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <FieldLabel>Tutor</FieldLabel>
                  <ArgonTypography variant="button" fontWeight="medium">
                    {tutorName(assignEligibility)}
                  </ArgonTypography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <FieldLabel>Badge</FieldLabel>
                  <ArgonTypography variant="button" fontWeight="medium">
                    {assignEligibility?.badgeName || "N/A"}
                  </ArgonTypography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <FieldLabel>Score</FieldLabel>
                  <ArgonTypography variant="button" fontWeight="medium">
                    {assignEligibility?.score ?? 0}
                  </ArgonTypography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <FieldLabel>Period</FieldLabel>
                  <ArgonTypography variant="button" fontWeight="medium">
                    {monthLabel(assignEligibility?.month)} {assignEligibility?.year}
                  </ArgonTypography>
                </Grid>
              </Grid>
              <ArgonTypography variant="caption" color="secondary" display="block" mt={1.5}>
                After confirmation, this badge will move from Approved to Assigned.
              </ArgonTypography>
            </ArgonBox>
          </DialogContent>
          <DialogActions sx={{ p: 3, pt: 0, gap: 1.5 }}>
            <ArgonButton
              onClick={() => setAssignConfirmDialog(false)}
              variant="outlined"
              color="secondary"
              disabled={saving}
            >
              Cancel
            </ArgonButton>
            <ArgonButton
              onClick={handleAssignApprovedEligibility}
              variant="contained"
              color="success"
              disabled={saving}
            >
              <CheckCircleIcon fontSize="small" />
              &nbsp;{saving ? "Assigning..." : "Assign Badge"}
            </ArgonButton>
          </DialogActions>
        </Dialog>

        <Dialog
          open={defaultConfirmDialog}
          onClose={() => setDefaultConfirmDialog(false)}
          maxWidth="sm"
          fullWidth
          sx={{
            "& .MuiDialog-paper": {
              borderRadius: "16px",
              boxShadow: "0 20px 60px rgba(0,0,0,0.18)",
            },
          }}
        >
          <DialogTitle sx={{ pb: 1, position: "relative" }}>
            <ArgonBox display="flex" alignItems="center" gap={2}>
              <ArgonBox
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: "12px",
                  backgroundColor: "rgba(17, 113, 239, 0.1)",
                  color: "#1171ef",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <RestartAltIcon />
              </ArgonBox>
              <ArgonBox>
                <ArgonTypography variant="h5" color="text" fontWeight="medium">
                  Apply Default Settings
                </ArgonTypography>
                <ArgonTypography variant="body2" color="secondary">
                  This will refresh the three default badge rules.
                </ArgonTypography>
              </ArgonBox>
            </ArgonBox>
            <IconButton
              onClick={() => setDefaultConfirmDialog(false)}
              sx={{ position: "absolute", right: 8, top: 8, color: "#6c757d" }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent sx={{ py: 2.5 }}>
            <ArgonBox
              sx={{
                backgroundColor: "#f8fbff",
                border: "1px solid #d7e7ff",
                borderRadius: "8px",
                p: 2,
              }}
            >
              <ArgonTypography variant="body2" color="text" fontWeight="medium">
                Teaching Champion, Homework Hero, and Always On Time settings will be created or
                updated with the default criteria.
              </ArgonTypography>
              <ArgonTypography variant="caption" color="secondary" display="block" mt={1}>
                Existing badge history and assigned badges will not be deleted.
              </ArgonTypography>
            </ArgonBox>
          </DialogContent>
          <DialogActions sx={{ p: 3, pt: 0, gap: 1.5 }}>
            <ArgonButton
              onClick={() => setDefaultConfirmDialog(false)}
              variant="outlined"
              color="secondary"
              disabled={saving}
            >
              Cancel
            </ArgonButton>
            <ArgonButton onClick={seedDefaults} variant="contained" color="primary" disabled={saving}>
              <RestartAltIcon fontSize="small" />
              &nbsp;{saving ? "Saving..." : "Apply Defaults"}
            </ArgonButton>
          </DialogActions>
        </Dialog>
      </ArgonBox>
      <Footer />
    </DashboardLayout>
  );
}

export default Gamification;

Gamification.propTypes = {
  mode: PropTypes.oneOf(["eligibility", "settings"]),
};
